<template>
  <figure class="h-chart" :style="{ height: resolvedHeight }">
    <div ref="el" class="h-chart__canvas" role="img" :aria-label="ariaLabel"></div>

    <!-- Visually-hidden table equivalent for screen readers (DESIGN.md: every
         chart carries a table/aria equivalent). Callers pass a <table> here. -->
    <figcaption class="h-chart__sr">
      <slot name="table" />
    </figcaption>
  </figure>
</template>

<script setup>
// ECharts wrapper initialised with the parallel 'hermes' theme (never the
// package's 'tc' theme). Props:
//   option  — an ECharts option object (reactive; re-applied on change)
//   height  — CSS height for the canvas (number = px)
// The #table slot holds a visually-hidden data table for accessibility.
//
// DELIBERATELY self-contained lifecycle (init / setOption / resize / dispose)
// instead of @apexl/icarus-dynamic-components' useChart composable. useChart
// stores the echarts instance in a deep `ref()`, so every call goes through a
// Vue reactive proxy — and calling `.resize()` through that proxy breaks
// echarts' internal identity-keyed lookups mid-re-render, crashing with
//   TypeError: Cannot read properties of undefined (reading 'type')
// in LineView.render (seriesModel.coordinateSystem lost). The ResizeObserver
// useChart attaches fires an initial size report immediately on observe(),
// so the crash hits on every mount. Verified by isolation (jsdom, browser
// render path): raw instance + hermes theme + resize passes; ref()-wrapped
// instance + DEFAULT theme + resize fails identically — so neither the theme
// nor the option is at fault. The instance below is a plain module variable
// (never reactive), which is the standard echarts+Vue3 guidance.
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import * as echarts from 'echarts';
import { registerHermesEchartsTheme, HERMES_THEME_NAME } from '@root/services/hermesEchartsTheme';

const props = defineProps({
  option: { type: Object, default: () => ({}) },
  height: { type: [String, Number], default: 320 },
  ariaLabel: { type: String, default: 'Chart' },
});

// Idempotent; index.js also registers at boot — safety net for charts that
// render before boot registration completed.
registerHermesEchartsTheme(echarts);

const el = ref(null);

// NON-reactive holders. Do not move these into ref()/reactive() — see above.
let chart = null;
let resizeObserver = null;
let resizeRaf = 0;

function applyOption() {
  if (!chart) return;
  const opt = props.option;
  if (!opt || !Object.keys(opt).length) return;
  try {
    // notMerge so each apply fully replaces the previous state (avoids
    // dangling series/coord-system refs from deep merges).
    chart.setOption(opt, { notMerge: true });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('HermesChart setOption threw', err);
  }
}

function resize() {
  if (!chart) return;
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => {
    if (chart) chart.resize();
  });
}

function initChart() {
  if (chart || !el.value) return;
  chart = echarts.init(el.value, HERMES_THEME_NAME);
  applyOption();

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(el.value);
  } else {
    window.addEventListener('resize', resize);
  }
}

// The element may sit behind a v-if in the parent — init when it appears.
watch(el, initChart, { immediate: true, flush: 'post' });
// Re-apply when the option (or anything inside it) changes.
watch(() => props.option, applyOption, { deep: true, flush: 'post' });

onBeforeUnmount(() => {
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  } else if (typeof window !== 'undefined') {
    window.removeEventListener('resize', resize);
  }
  if (chart) {
    chart.dispose();
    chart = null;
  }
});

const resolvedHeight = computed(() =>
  typeof props.height === 'number' ? `${props.height}px` : props.height
);
</script>

<style scoped>
.h-chart { margin: 0; position: relative; width: 100%; }
.h-chart__canvas { width: 100%; height: 100%; }
.h-chart__sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
</style>
